/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useEffect, forwardRef, useMemo } from 'react';
import { Input, Skeleton } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { SearchOutlined } from '@ant-design/icons';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import CampaignCard from '@components/CampaignCard';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';
import LottieComponent from '@components/Lottie';
import { DraggableItem } from '@components/DraggableList';
import { DraggableList } from '@components/DraggableList/DraggableList';
import { useDebounced } from '@helpers/useDebounced';
import { useCampaignsQuery } from '@hooks/queries/campaigns';

function CampaignsListEmptyState() {
  return (
    <div className="empty_state">
      <div className="empty_state-content tour-campaign-empty-state">
        <LottieComponent className="campaign-icon" view="addCampaign" />
        <div className="title">There are no projects yet</div>
      </div>
    </div>
  );
}

const CampaignsListSkeleton = forwardRef<HTMLDivElement>(
  function CampaignsListSkeleton(props, ref) {
    return (
      <div ref={ref} className="campaign_card_container">
        <>
          <div className="assets assets_0">
            <Skeleton.Input style={{ width: '100%', height: 136 }} />
          </div>
          <div className="campaign_info">
            <Skeleton.Input style={{ width: '100%', height: 42 }} />
          </div>
        </>
      </div>
    );
  }
);

function CampaignsListInWorkspace() {
  const [currentWorkspace] = useCurrentWorkspace(true);
  const workspaceId = currentWorkspace.id;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const queryClient = useQueryClient();
  const campaignsQuery = useCampaignsQuery(
    {
      workspaceId,
      searchQuery: debouncedSearchQuery,
      orderBy: ['order:DESC']
    },
    { keepPreviousData: true }
  );
  const { isLoading } = campaignsQuery;
  const campaignsList = useMemo(() => {
    return campaignsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [campaignsQuery.data]);

  const skeletonsCount = campaignsQuery.hasNextPage === false ? 0 : 10;
  const isEmpty = !isLoading && campaignsList.length === 0;

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-access-changed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== workspaceId) return false;
      if (msg.causedByMe && msg.payload.reason !== 'conversion-status-changed')
        return false;
      return true;
    },
    'asset-updated'
  );

  return (
    <>
      <div className="inner-header">
        <div className="inner-header__name">
          <span className="inner-header__title">SELECT PROJECT</span>
        </div>
      </div>
      <Input
        size="large"
        placeholder="Search projects"
        prefix={<SearchOutlined />}
        className="search_input"
        onChange={(event) => setSearchQuery(event.target.value)}
        value={searchQuery}
        style={{ marginTop: 18 }}
      />
      <div className="campaign_cards">
        <div className="campaign_cards-content" style={{ height: 'auto' }}>
          <InfiniteScroll
            loading={campaignsQuery.isFetchingNextPage ? 'more' : false}
            next={campaignsQuery.fetchNextPage}
            hasMore={campaignsQuery.hasNextPage ?? false}
          >
            {isEmpty ? (
              <CampaignsListEmptyState />
            ) : (
              <DraggableList
                className="campaign_card_list"
                gap={16}
                disableReorder={true}
              >
                {!isLoading
                  ? campaignsList
                      .concat(
                        new Array(
                          campaignsQuery.isFetchingNextPage ? skeletonsCount : 0
                        ).fill('')
                      )
                      .map(({ node }, i) => {
                        if (!node) {
                          return (
                            <CampaignsListSkeleton key={`skeleton-${i}`} />
                          );
                        }
                        return (
                          <DraggableItem
                            key={node.id}
                            id={node.id}
                            type="campaign"
                          >
                            <CampaignCard
                              campaign={node}
                              kind="simple"
                              onClick={() => {
                                const { uxpHost } = window as any;
                                uxpHost.postMessage({
                                  type: 'select-campaign',
                                  id: node.id
                                });
                              }}
                            />
                          </DraggableItem>
                        );
                      })
                  : new Array(skeletonsCount)
                      .fill('')
                      .map((el, i) => (
                        <CampaignsListSkeleton key={`skeleton-${i}`} />
                      ))}
              </DraggableList>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </>
  );
}

function CampaignsList() {
  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  useEffect(() => {
    if (!currentWorkspace && defaultWorkspace) {
      setCurrentWorkspace(defaultWorkspace.id);
    }
  }, [defaultWorkspace, currentWorkspace, setCurrentWorkspace]);
  return currentWorkspace ? <CampaignsListInWorkspace /> : null;
}

export default CampaignsList;
